import { notification } from 'antd'
import { Icon } from '@iconify/react'
import './ActionNotification.css'

/** Above task detail fullscreen overlay (projectDetail uses z-index 9999). */
export const NOTIFICATION_Z_ABOVE_FULLSCREEN = 10050

const TYPE_TOP_BORDER_COLORS = {
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
}

const elevateNotificationRoots = (zIndex) => {
    if (zIndex == null) return
    const z = String(zIndex)
    const bump = () => {
        document.querySelectorAll('.ant-notification').forEach((el) => {
            el.style.zIndex = z
        })
    }
    bump()
    requestAnimationFrame(bump)
}

/**
 * Global notification component with action button (e.g., Undo)
 * Can be used anywhere in the project for any undo/action scenario
 *
 * @param {Object} params
 * @param {string} params.type - Notification type: success | error | warning | info (default: 'success')
 * @param {string|ReactNode} params.message - Main message to display
 * @param {string|ReactNode} params.subMessage - Optional sub-message (e.g., ID, reference)
 * @param {string} params.actionButtonText - Text for the action button (default: 'Undo')
 * @param {Function} params.onAction - Callback function when action button is clicked
 * @param {string} params.placement - Notification placement (default: 'bottomLeft')
 * @param {number} params.duration - Auto-close duration in seconds (0 = don't auto-close, default: 5)
 * @param {string} params.notificationKey - Unique key for the notification
 * @param {Object} params.style - Custom styles for the notification
 * @param {ReactNode} params.closeIcon - Custom close icon
 * @param {string} params.subMessageColor - Color for sub-message (default: '#3b82f6')
 */
export const showActionNotification = ({
    type = 'success',
    message: notificationMessage,
    subMessage,
    actionButtonText = 'Undo',
    onAction,
    placement = 'bottomLeft',
    duration = 5,
    notificationKey = 'action-notification',
    style = {},
    closeIcon,
    subMessageColor = '#4f46e5',
    zIndex,
}) => {
    const normalizedType = String(type || 'success')
        .trim()
        .toLowerCase()
    const topBorderColor =
        TYPE_TOP_BORDER_COLORS[normalizedType] ||
        TYPE_TOP_BORDER_COLORS.success

    const effectiveZIndex = zIndex ?? undefined
    const defaultStyle = {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        width: '320px',
        borderTop: `4px solid ${topBorderColor}`,
        ...(effectiveZIndex != null ? { zIndex: effectiveZIndex } : {}),
        ...style,
    }

    const defaultCloseIcon = (
        <Icon icon="mdi:close" style={{ fontSize: '16px', color: '#6b7280' }} />
    )

    const messageContent = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
            }}
        >
            <span
                style={{
                    fontSize: '14px',
                    color: '#1f2937',
                    fontWeight: 400,
                }}
            >
                {typeof notificationMessage === 'string'
                    ? notificationMessage
                    : notificationMessage != null &&
                        typeof notificationMessage === 'object' &&
                        typeof notificationMessage.error === 'string'
                      ? notificationMessage.error
                      : typeof notificationMessage === 'object'
                        ? JSON.stringify(notificationMessage)
                        : String(notificationMessage ?? '')}
            </span>
            {subMessage && (
                <span
                    style={{
                        fontSize: '14px',
                        color: subMessageColor,
                        fontWeight: 500,
                    }}
                >
                    {subMessage}
                </span>
            )}
        </div>
    )

    notification.open({
        message: messageContent,
        description: null,
        placement,
        duration,
        key: notificationKey,
        className: `action-notification action-notification--${normalizedType}`,
        style: defaultStyle,
        closeIcon: closeIcon || defaultCloseIcon,
        ...(zIndex !== undefined && { zIndex }),
        btn: onAction ? (
            <button
                onClick={async () => {
                    try {
                        await onAction()
                        notification.close(notificationKey)
                    } catch (error) {
                        console.error('Action notification error:', error)
                    }
                }}
                style={{
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1f2937',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb'
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6'
                }}
            >
                {actionButtonText}
            </button>
        ) : null,
    })

    if (effectiveZIndex != null) {
        elevateNotificationRoots(effectiveZIndex)
    }
}

/**
 * Helper function to close a specific notification by key
 * @param {string} notificationKey - The key of the notification to close
 */
export const closeActionNotification = (notificationKey) => {
    notification.close(notificationKey)
}
